import React from 'react';
import PauseIcon from '@mui/icons-material/Pause';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import { getBytes, getDownloadURL, ref } from 'firebase/storage';
import { storage } from '../firebase';
import { TherapySession, TherapySessionPlacedTag, TherapySessionTagDefinition, TherapySessionTranscriptUtterance, TherapySessionWaveformPeak } from '../interfaces/Frameworks';
import { Theme } from '../Theme';

type SpeakerKind = 'therapist' | 'client' | 'other';

type WaveformBar = {
    height: number;
    speakerKind: SpeakerKind;
};

type SessionAudioPlayerProps = {
    session: TherapySession;
    utterances: Array<TherapySessionTranscriptUtterance>;
    availableTags?: Array<TherapySessionTagDefinition>;
    onCreateTag?: (label: string) => Promise<void>;
    onPlaceTag?: (session: TherapySession, tag: TherapySessionTagDefinition, timestampMs: number) => Promise<void>;
    onRemovePlacedTag?: (session: TherapySession, placedTagId: string) => Promise<void>;
    onPlaybackTimeChange?: (sessionId: string, currentTimeMs: number) => void;
    onEnsureWaveform?: (sessionId: string) => Promise<void>;
    seekRequest?: {
        timestampMs: number;
        requestId: number;
    };
};

const WAVEFORM_BAR_COUNT = 220;
const WAVEFORM_LOAD_TIMEOUT_MS = 120000;

// Do not ask the browser waveform decoder to download or decode video containers.
function isSessionMediaVideo(session: TherapySession): boolean {
    const contentType = (
        session.audioContentType ||
        session.importMediaContentType ||
        session.originalMediaContentType ||
        ''
    ).toLowerCase();
    const storagePath = session.storagePath || session.importMediaStoragePath || '';
    if (contentType.startsWith('audio/')) {
        return false;
    }

    if (contentType.startsWith('video/')) {
        return true;
    }

    return /\.(mp4|m4v|mov|mkv|avi)$/i.test(storagePath);
}

// Explain why playback is waiting without starting a large video waveform download.
function getMissingAudioMessage(session: TherapySession): string {
    if (session.source === 'import' && (session.transcriptStatus === 'error' || session.transcriptStatus === 'skipped')) {
        return 'Recording processing did not complete.';
    }
    if (session.source === 'import' && (
        session.transcriptStatus === 'pending' ||
        session.transcriptStatus === 'processing' ||
        !!session.importMediaStoragePath ||
        isSessionMediaVideo(session)
    )) {
        return 'Processing recording…';
    }

    return 'Audio recording unavailable.';
}

// Imported files should use server-generated peaks instead of large browser downloads.
function shouldGenerateWaveformOnServer(session: TherapySession): boolean {
    return session.source === 'import' ||
        !!session.originalFileName ||
        !!session.originalMediaContentType ||
        !!session.importMediaStoragePath;
}

// Keep time labels compact while still handling longer in-office sessions.
function formatAudioTime(timeSeconds: number): string {
    if (!Number.isFinite(timeSeconds) || timeSeconds <= 0) {
        return '0:00';
    }

    const totalSeconds = Math.floor(timeSeconds);
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;

    if (hours > 0) {
        return `${hours}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
    }

    return `${minutes}:${String(seconds).padStart(2, '0')}`;
}

// Reuse the audio time formatter for millisecond tag marker labels.
function formatTagTime(timeMs: number): string {
    return formatAudioTime(timeMs / 1000);
}

// Keep placed tag markers inside the visible waveform bounds.
function getPlacedTagLeftPercent(tag: TherapySessionPlacedTag, durationSeconds: number): number {
    if (!Number.isFinite(durationSeconds) || durationSeconds <= 0) {
        return 0;
    }

    return Math.min(100, Math.max(0, (tag.timestampMs / (durationSeconds * 1000)) * 100));
}

// Mix saved tag colours with white so even older saturated tags render softly.
function getLightTagColour(color: string): string {
    const hex = color.replace('#', '').trim();
    if (!/^[0-9a-fA-F]{6}$/.test(hex)) {
        return color || '#e5e7eb';
    }

    const red = parseInt(hex.slice(0, 2), 16);
    const green = parseInt(hex.slice(2, 4), 16);
    const blue = parseInt(hex.slice(4, 6), 16);
    const mixWithWhite = (channel: number) => Math.round(channel + (255 - channel) * 0.72);

    return `rgb(${mixWithWhite(red)}, ${mixWithWhite(green)}, ${mixWithWhite(blue)})`;
}

// Keep a subtle border in the same colour family as the tag fill.
function getTagBorderColour(color: string): string {
    const hex = color.replace('#', '').trim();
    if (!/^[0-9a-fA-F]{6}$/.test(hex)) {
        return color || '#d1d5db';
    }

    const red = parseInt(hex.slice(0, 2), 16);
    const green = parseInt(hex.slice(2, 4), 16);
    const blue = parseInt(hex.slice(4, 6), 16);
    const mixWithWhite = (channel: number) => Math.round(channel + (255 - channel) * 0.42);

    return `rgb(${mixWithWhite(red)}, ${mixWithWhite(green)}, ${mixWithWhite(blue)})`;
}

// Match waveform colours to the same speaker roles as the transcript.
function getUtteranceSpeakerKind(utterance: TherapySessionTranscriptUtterance): SpeakerKind {
    if (utterance.speakerAssignment?.transcriptRole) {
        return utterance.speakerAssignment.transcriptRole;
    }

    const role = (utterance.role || '').toLowerCase();
    if (role.includes('therapist')) {
        return 'therapist';
    }

    if (role.includes('client')) {
        return 'client';
    }

    if (role === 'unknown' || role === 'other') {
        return 'other';
    }

    if (utterance.channel === 1) return 'therapist';
    if (utterance.channel === 2) return 'client';

    return 'other';
}

// Find the speaker occupying a waveform bar's midpoint.
function getSpeakerKindAtTime(
    utterances: Array<TherapySessionTranscriptUtterance>,
    timeMs: number
): SpeakerKind {
    const utterance = utterances.find((candidate) => (
        candidate.startMs <= timeMs &&
        candidate.endMs >= timeMs
    ));

    return utterance ? getUtteranceSpeakerKind(utterance) : 'other';
}

// Convert decoded audio into a fixed-width visual waveform.
function buildWaveformBars(
    audioBuffer: AudioBuffer,
    utterances: Array<TherapySessionTranscriptUtterance>
): Array<WaveformBar> {
    const samples = audioBuffer.getChannelData(0);
    const samplesPerBar = Math.max(1, Math.floor(samples.length / WAVEFORM_BAR_COUNT));
    const durationMs = audioBuffer.duration * 1000;
    const rawBars = Array.from({ length: WAVEFORM_BAR_COUNT }, (_unused, barIndex) => {
        const startSample = barIndex * samplesPerBar;
        const endSample = Math.min(samples.length, startSample + samplesPerBar);
        let sum = 0;

        for (let sampleIndex = startSample; sampleIndex < endSample; sampleIndex += 1) {
            sum += samples[sampleIndex] * samples[sampleIndex];
        }

        const sampleCount = Math.max(1, endSample - startSample);
        const rms = Math.sqrt(sum / sampleCount);
        const midpointMs = ((barIndex + 0.5) / WAVEFORM_BAR_COUNT) * durationMs;
        return {
            rms,
            speakerKind: getSpeakerKindAtTime(utterances, midpointMs),
        };
    });
    const maxRms = Math.max(...rawBars.map((bar) => bar.rms), 0.001);

    return rawBars.map((bar) => ({
        speakerKind: bar.speakerKind,
        height: Math.max(12, Math.round((bar.rms / maxRms) * 100)),
    }));
}

// Convert saved recording-time peaks into the same fixed-width waveform bars.
function buildWaveformBarsFromPeaks(
    peaks: Array<TherapySessionWaveformPeak>,
    durationMs: number,
    utterances: Array<TherapySessionTranscriptUtterance>
): Array<WaveformBar> {
    const effectiveDurationMs = Math.max(
        durationMs,
        ...peaks.map((peak) => peak.endMs),
        1
    );
    const rawBars = Array.from({ length: WAVEFORM_BAR_COUNT }, (_unused, barIndex) => {
        const startMs = (barIndex / WAVEFORM_BAR_COUNT) * effectiveDurationMs;
        const endMs = ((barIndex + 1) / WAVEFORM_BAR_COUNT) * effectiveDurationMs;
        const overlappingPeaks = peaks.filter((peak) => (
            peak.endMs >= startMs &&
            peak.startMs <= endMs
        ));
        const amplitude = overlappingPeaks.length > 0 ?
            Math.max(...overlappingPeaks.map((peak) => peak.amplitude)) :
            0;

        return {
            amplitude,
            speakerKind: getSpeakerKindAtTime(utterances, (startMs + endMs) / 2),
        };
    });
    const maxAmplitude = Math.max(...rawBars.map((bar) => bar.amplitude), 0.001);

    return rawBars.map((bar) => ({
        speakerKind: bar.speakerKind,
        height: Math.max(12, Math.round((bar.amplitude / maxAmplitude) * 100)),
    }));
}

// Resolve a browser AudioContext constructor across Chromium and Safari.
function getAudioContextConstructor(): typeof AudioContext | null {
    return window.AudioContext || (window as any).webkitAudioContext || null;
}

// Colour each waveform slice by speaker identity.
function getWaveformBarColour(speakerKind: SpeakerKind): string {
    if (speakerKind === 'therapist') {
        return '#2563eb';
    }

    if (speakerKind === 'client') {
        return '#f97316';
    }

    return 'rgba(0, 0, 0, 0.28)';
}

// Prevent waveform generation from leaving the UI in a permanent loading state.
function withTimeout<T>(promise: Promise<T>, timeoutMs: number, errorMessage: string): Promise<T> {
    return new Promise((resolve, reject) => {
        const timeoutId = window.setTimeout(() => {
            reject(new Error(errorMessage));
        }, timeoutMs);

        promise
            .then((value) => {
                window.clearTimeout(timeoutId);
                resolve(value);
            })
            .catch((error) => {
                window.clearTimeout(timeoutId);
                reject(error);
            });
    });
}

// Keep the visible failure compact but useful while preserving the full console error.
function getWaveformErrorMessage(error: unknown): string {
    const errorMessage = error instanceof Error ? error.message : String(error || '');
    if (errorMessage.toLowerCase().includes('permission')) {
        return 'Waveform unavailable: storage permission denied.';
    }

    if (errorMessage.toLowerCase().includes('timed out')) {
        return 'Waveform unavailable: audio download timed out.';
    }

    if (errorMessage.toLowerCase().includes('decode')) {
        return 'Waveform unavailable: browser could not decode this recording.';
    }

    return 'Waveform unavailable.';
}

export function SessionAudioPlayer(props: SessionAudioPlayerProps) {
    const {
        session,
        utterances,
        availableTags = [],
        onCreateTag,
        onPlaceTag,
        onRemovePlacedTag,
        onPlaybackTimeChange,
        onEnsureWaveform,
        seekRequest,
    } = props;
    const audioRef = React.useRef<HTMLAudioElement | null>(null);
    const utterancesRef = React.useRef(utterances);
    const skipNextTagCreateRef = React.useRef(false);
    const ensuredWaveformSessionIdsRef = React.useRef<Set<string>>(new Set());
    const [audioUrl, setAudioUrl] = React.useState('');
    const [waveformBars, setWaveformBars] = React.useState<Array<WaveformBar>>([]);
    const [isLoadingWaveform, setIsLoadingWaveform] = React.useState(false);
    const [waveformError, setWaveformError] = React.useState('');
    const [isPlaying, setIsPlaying] = React.useState(false);
    const [currentTime, setCurrentTime] = React.useState(0);
    const [isCreatingTag, setIsCreatingTag] = React.useState(false);
    const [newTagLabel, setNewTagLabel] = React.useState('');
    const [hoveredPlacedTagId, setHoveredPlacedTagId] = React.useState<string | null>(null);
    const [duration, setDuration] = React.useState(
        session.durationMs ? session.durationMs / 1000 : 0
    );
    const placedTags = [...(session.placedTags || [])].sort((leftTag, rightTag) => leftTag.timestampMs - rightTag.timestampMs);
    const utteranceSignature = utterances
        .map((utterance) => `${utterance.role}:${utterance.channel}:${utterance.startMs}-${utterance.endMs}:${utterance.text}`)
        .join('|');
    const sessionStoragePath = session.storagePath || '';
    const sessionDurationMs = session.durationMs || 0;
    const sessionWaveformPeaks = session.waveformPeaks;
    const sessionMediaIsVideo = isSessionMediaVideo(session);
    const missingAudioMessage = getMissingAudioMessage(session);
    const shouldUseServerWaveform = shouldGenerateWaveformOnServer(session);

    // Keep parent transcript highlighting in sync with the audio cursor.
    const updateCurrentTime = React.useCallback((nextCurrentTime: number) => {
        setCurrentTime(nextCurrentTime);
        onPlaybackTimeChange?.(session.id, Math.round(nextCurrentTime * 1000));
    }, [onPlaybackTimeChange, session.id]);

    React.useEffect(() => {
        // Parent highlighting re-renders create new arrays; keep the latest content without reloading audio.
        utterancesRef.current = utterances;
    }, [utteranceSignature, utterances]);

    React.useEffect(() => {
        let isCancelled = false;

        async function loadAudio() {
            if (!sessionStoragePath) {
                setAudioUrl('');
                setWaveformBars([]);
                setWaveformError(missingAudioMessage);
                return;
            }

            if (sessionMediaIsVideo) {
                setAudioUrl('');
                setWaveformBars([]);
                setWaveformError(missingAudioMessage);
                return;
            }

            if (sessionWaveformPeaks?.length) {
                // Saved peaks are enough to draw immediately; keep them visible while the audio URL loads.
                setIsLoadingWaveform(false);
                setWaveformError('');
                setWaveformBars(buildWaveformBarsFromPeaks(sessionWaveformPeaks, sessionDurationMs, utterancesRef.current));

                try {
                    const storageRef = ref(storage, sessionStoragePath);
                    const downloadUrl = await withTimeout(
                        getDownloadURL(storageRef),
                        WAVEFORM_LOAD_TIMEOUT_MS,
                        'Timed out while loading session audio URL.'
                    );

                    if (!isCancelled) {
                        setAudioUrl(downloadUrl);
                    }
                } catch (error) {
                    console.error('SESSION_AUDIO_PLAYER: Failed to load audio URL:', error);
                    if (!isCancelled) {
                        setWaveformError(getWaveformErrorMessage(error));
                    }
                }
                return;
            }

            setIsLoadingWaveform(true);
            setWaveformError('');
            setWaveformBars([]);

            try {
                const storageRef = ref(storage, sessionStoragePath);
                const downloadUrl = await withTimeout(
                    getDownloadURL(storageRef),
                    WAVEFORM_LOAD_TIMEOUT_MS,
                    'Timed out while loading session audio URL.'
                );

                if (isCancelled) {
                    return;
                }

                setAudioUrl(downloadUrl);
                if (shouldUseServerWaveform && onEnsureWaveform) {
                    if (ensuredWaveformSessionIdsRef.current.has(session.id)) {
                        setIsLoadingWaveform(false);
                        setWaveformError('Waveform unavailable.');
                        return;
                    }

                    ensuredWaveformSessionIdsRef.current.add(session.id);
                    setIsLoadingWaveform(false);
                    setWaveformError('Generating waveform...');
                    try {
                        await onEnsureWaveform(session.id);
                    } catch (error) {
                        console.error('SESSION_AUDIO_PLAYER: Failed to generate server waveform:', error);
                        if (!isCancelled) {
                            setWaveformError(getWaveformErrorMessage(error));
                        }
                    }
                    return;
                }

                const AudioContextConstructor = getAudioContextConstructor();
                if (!AudioContextConstructor) {
                    throw new Error('Audio decoding is not supported in this browser.');
                }

                const audioBytes = await withTimeout(
                    getBytes(storageRef),
                    WAVEFORM_LOAD_TIMEOUT_MS,
                    'Timed out while downloading session audio for waveform.'
                );
                const audioContext = new AudioContextConstructor();
                const audioBuffer = await withTimeout(
                    audioContext.decodeAudioData(audioBytes.slice(0)),
                    WAVEFORM_LOAD_TIMEOUT_MS,
                    'Timed out while decoding session audio for waveform.'
                );
                await audioContext.close();

                if (!isCancelled) {
                    setDuration(audioBuffer.duration);
                    setWaveformBars(buildWaveformBars(audioBuffer, utterancesRef.current));
                }
            } catch (error) {
                console.error('SESSION_AUDIO_PLAYER: Failed to load waveform:', error);
                if (!isCancelled) {
                    setWaveformError(getWaveformErrorMessage(error));
                }
            } finally {
                if (!isCancelled) {
                    setIsLoadingWaveform(false);
                }
            }
        }

        void loadAudio();

        return () => {
            isCancelled = true;
        };
    }, [
        missingAudioMessage,
        sessionDurationMs,
        sessionMediaIsVideo,
        sessionStoragePath,
        sessionWaveformPeaks,
        shouldUseServerWaveform,
        onEnsureWaveform,
        session.id,
        utteranceSignature,
    ]);

    React.useEffect(() => {
        // Clinical-note timestamp buttons can seek the waveform without starting playback.
        if (!seekRequest) {
            return;
        }

        const nextCurrentTime = Math.max(0, seekRequest.timestampMs / 1000);
        if (audioRef.current) {
            audioRef.current.currentTime = duration > 0 ?
                Math.min(duration, nextCurrentTime) :
                nextCurrentTime;
        }
        updateCurrentTime(duration > 0 ? Math.min(duration, nextCurrentTime) : nextCurrentTime);
    }, [duration, seekRequest, updateCurrentTime]);

    const progress = duration > 0 ? Math.min(1, currentTime / duration) : 0;

    // Toggle playback using the hidden audio element that owns browser audio output.
    const handleTogglePlayback = async () => {
        const audio = audioRef.current;
        if (!audio || !audioUrl) {
            return;
        }

        if (audio.paused) {
            try {
                await audio.play();
            } catch (error) {
                console.error('SESSION_AUDIO_PLAYER: Failed to play audio:', error);
            }
        } else {
            audio.pause();
        }
    };

    // Seek the recording to the clicked position in the waveform.
    const handleWaveformClick = (event: React.MouseEvent<HTMLDivElement>) => {
        const audio = audioRef.current;
        if (!audio || duration <= 0) {
            return;
        }

        const bounds = event.currentTarget.getBoundingClientRect();
        const clickProgress = Math.min(1, Math.max(0, (event.clientX - bounds.left) / bounds.width));
        audio.currentTime = clickProgress * duration;
        updateCurrentTime(audio.currentTime);
    };

    // Create a therapist-owned tag from the inline editor.
    const handleCreateTag = async () => {
        if (skipNextTagCreateRef.current) {
            skipNextTagCreateRef.current = false;
            return;
        }

        const trimmedLabel = newTagLabel.trim();
        if (!trimmedLabel || !onCreateTag) {
            setIsCreatingTag(false);
            setNewTagLabel('');
            return;
        }

        try {
            await onCreateTag(trimmedLabel);
            setNewTagLabel('');
            setIsCreatingTag(false);
        } catch (error) {
            console.error('SESSION_AUDIO_PLAYER: Failed to create tag:', error);
            window.alert(error instanceof Error ? error.message : 'Could not create the session tag.');
        }
    };

    // Place a tag marker at the current audio cursor.
    const handlePlaceTag = async (tag: TherapySessionTagDefinition) => {
        if (!onPlaceTag) {
            return;
        }

        try {
            await onPlaceTag(session, tag, Math.round(currentTime * 1000));
        } catch (error) {
            console.error('SESSION_AUDIO_PLAYER: Failed to place tag:', error);
            window.alert(error instanceof Error ? error.message : 'Could not place the session tag.');
        }
    };

    // Remove one already-placed marker from the waveform.
    const handleRemovePlacedTag = async (placedTagId: string) => {
        if (!onRemovePlacedTag) {
            return;
        }

        try {
            await onRemovePlacedTag(session, placedTagId);
        } catch (error) {
            console.error('SESSION_AUDIO_PLAYER: Failed to remove placed tag:', error);
            window.alert(error instanceof Error ? error.message : 'Could not remove the session tag.');
        }
    };

    return (
        <div style={styles.audioPlayer}>
            <audio
                ref={audioRef}
                src={audioUrl}
                preload='metadata'
                onPlay={() => setIsPlaying(true)}
                onPause={() => setIsPlaying(false)}
                onEnded={() => setIsPlaying(false)}
                onTimeUpdate={(event) => updateCurrentTime(event.currentTarget.currentTime)}
                onLoadedMetadata={(event) => {
                    const nextDuration = event.currentTarget.duration || duration;
                    setDuration(nextDuration);
                    if (currentTime > 0) {
                        event.currentTarget.currentTime = nextDuration > 0 ?
                            Math.min(nextDuration, currentTime) :
                            currentTime;
                    }
                }}
            />

            <div style={styles.audioControlsRow}>
                <button
                    type='button'
                    onClick={handleTogglePlayback}
                    disabled={!audioUrl}
                    style={{
                        ...styles.playButton,
                        ...(!audioUrl ? styles.disabledPlayButton : {}),
                    }}
                    aria-label={isPlaying ? 'Pause session audio' : 'Play session audio'}
                >
                    {isPlaying ? <PauseIcon fontSize='small' /> : <PlayArrowIcon fontSize='small' />}
                </button>

                <div style={styles.waveformColumn}>
                    <div
                        role='button'
                        tabIndex={waveformBars.length === 0 ? -1 : 0}
                        onClick={handleWaveformClick}
                        onKeyDown={(event) => {
                            if (event.key === 'Enter' || event.key === ' ') {
                                event.preventDefault();
                                void handleTogglePlayback();
                            }
                        }}
                        style={{
                            ...styles.waveformButton,
                            ...(waveformBars.length === 0 ? styles.disabledWaveformButton : {}),
                        }}
                        aria-label='Seek session audio'
                    >
                        {waveformBars.length > 0 ? (
                            <div style={styles.waveformArea}>
                                <div style={styles.waveformBars}>
                                    {waveformBars.map((bar, barIndex) => {
                                        const isPlayed = barIndex / waveformBars.length <= progress;
                                        return (
                                            <span
                                                key={`${session.id}-wave-${barIndex}`}
                                                style={{
                                                    ...styles.waveformBar,
                                                    height: `${bar.height}%`,
                                                    backgroundColor: getWaveformBarColour(bar.speakerKind),
                                                    opacity: isPlayed ? 1 : 0.38,
                                                }}
                                            />
                                        );
                                    })}
                                    <span style={{ ...styles.progressMarker, left: `${progress * 100}%` }} />
                                </div>
                                {placedTags.map((placedTag) => {
                                    const isHoveredPlacedTag = hoveredPlacedTagId === placedTag.id;
                                    return (
                                        <span
                                            key={placedTag.id}
                                            onClick={(event) => event.stopPropagation()}
                                            onMouseEnter={() => setHoveredPlacedTagId(placedTag.id)}
                                            onMouseLeave={() => setHoveredPlacedTagId(null)}
                                            style={{
                                                ...styles.placedTagStack,
                                                left: `${getPlacedTagLeftPercent(placedTag, duration)}%`,
                                            }}
                                            title={`${placedTag.label} at ${formatTagTime(placedTag.timestampMs)}`}
                                        >
                                            <span
                                                style={{
                                                    ...styles.placedTagConnector,
                                                    backgroundColor: getTagBorderColour(placedTag.color),
                                                }}
                                            />
                                            <span
                                                style={{
                                                    ...styles.placedTagMarker,
                                                    backgroundColor: getLightTagColour(placedTag.color),
                                                    borderColor: getTagBorderColour(placedTag.color),
                                                }}
                                            >
                                                <span style={styles.placedTagLabel}>
                                                    {placedTag.label}
                                                </span>
                                                {isHoveredPlacedTag && onRemovePlacedTag && (
                                                    <button
                                                        type='button'
                                                        onClick={(event) => {
                                                            event.stopPropagation();
                                                            void handleRemovePlacedTag(placedTag.id);
                                                        }}
                                                        style={styles.removePlacedTagButton}
                                                        aria-label={`Remove ${placedTag.label} tag`}
                                                    >
                                                        ×
                                                    </button>
                                                )}
                                            </span>
                                        </span>
                                    );
                                })}
                            </div>
                        ) : (
                            <div style={styles.waveformPlaceholder}>
                                {isLoadingWaveform ? 'Loading waveform...' : waveformError || 'Audio waveform unavailable.'}
                            </div>
                        )}
                    </div>

                    {/* Read-only players omit tag controls instead of exposing no-op actions. */}
                    {(onCreateTag || (availableTags.length > 0 && onPlaceTag)) && (
                    <div style={styles.tagTray}>
                        {availableTags.map((tag) => (
                            <button
                                key={tag.id}
                                type='button'
                                onClick={() => void handlePlaceTag(tag)}
                                style={{
                                    ...styles.availableTagButton,
                                    backgroundColor: getLightTagColour(tag.color),
                                    borderColor: getTagBorderColour(tag.color),
                                }}
                                title={`Place "${tag.label}" at ${formatAudioTime(currentTime)}`}
                            >
                                {tag.label}
                            </button>
                        ))}
                        {isCreatingTag ? (
                            <input
                                value={newTagLabel}
                                onChange={(event) => setNewTagLabel(event.target.value)}
                                onKeyDown={(event) => {
                                    if (event.key === 'Enter') {
                                        event.preventDefault();
                                        event.currentTarget.blur();
                                    }

                                    if (event.key === 'Escape') {
                                        event.preventDefault();
                                        skipNextTagCreateRef.current = true;
                                        setIsCreatingTag(false);
                                        setNewTagLabel('');
                                    }
                                }}
                                onBlur={() => void handleCreateTag()}
                                autoFocus
                                placeholder='New tag'
                                style={styles.newTagInput}
                            />
                        ) : (
                            <button
                                type='button'
                                onClick={() => setIsCreatingTag(true)}
                                style={styles.addTagButton}
                                aria-label='Create session tag'
                                title='Create session tag'
                            >
                                +
                            </button>
                        )}
                    </div>
                    )}
                </div>

                <div style={styles.timeLabel}>
                    {formatAudioTime(currentTime)} / {formatAudioTime(duration)}
                </div>
            </div>
        </div>
    );
}

const styles: Record<string, React.CSSProperties> = {
    audioPlayer: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'stretch',
        gap: '12px',
        width: '100%',
        padding: '12px 14px',
        borderRadius: '8px',
        border: Theme.Colours.border,
        backgroundColor: Theme.Colours.background,
        boxSizing: 'border-box',
    },
    audioControlsRow: {
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        width: '100%',
    },
    playButton: {
        width: '38px',
        height: '38px',
        borderRadius: '50%',
        border: '1px solid rgba(0, 0, 0, 0.12)',
        backgroundColor: '#ffffff',
        color: Theme.Colours.text,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        cursor: 'pointer',
        flexShrink: 0,
    },
    disabledPlayButton: {
        cursor: 'not-allowed',
        opacity: 0.45,
    },
    waveformColumn: {
        display: 'flex',
        flexDirection: 'column',
        gap: '8px',
        flex: 1,
        minWidth: 0,
    },
    waveformButton: {
        flex: 1,
        minWidth: 0,
        height: '94px',
        backgroundColor: 'transparent',
        cursor: 'pointer',
    },
    disabledWaveformButton: {
        cursor: 'default',
    },
    waveformArea: {
        position: 'relative',
        width: '100%',
        height: '94px',
    },
    waveformBars: {
        position: 'relative',
        width: '100%',
        height: '54px',
        display: 'flex',
        alignItems: 'center',
        gap: '2px',
        overflow: 'hidden',
    },
    waveformBar: {
        flex: '1 1 0',
        minWidth: '2px',
        borderRadius: '2px',
        transition: 'opacity 0.12s ease',
    },
    progressMarker: {
        position: 'absolute',
        top: '4px',
        bottom: '4px',
        width: '2px',
        backgroundColor: 'rgba(0, 0, 0, 0.62)',
        transform: 'translateX(-1px)',
        pointerEvents: 'none',
    },
    placedTagStack: {
        position: 'absolute',
        top: '27px',
        display: 'inline-flex',
        flexDirection: 'column',
        alignItems: 'center',
        transform: 'translateX(-50%)',
        zIndex: 2,
        pointerEvents: 'none',
    },
    placedTagConnector: {
        width: '2px',
        height: '31px',
        opacity: 0.85,
        pointerEvents: 'none',
    },
    placedTagMarker: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: '2px',
        maxWidth: '132px',
        border: '1px solid',
        borderRadius: '999px',
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.14)',
        overflow: 'hidden',
        pointerEvents: 'auto',
    },
    placedTagLabel: {
        ...Theme.Fonts.body,
        color: Theme.Colours.text,
        fontSize: '11px',
        fontWeight: 700,
        maxWidth: '118px',
        overflow: 'hidden',
        padding: '3px 8px',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
    },
    removePlacedTagButton: {
        ...Theme.Fonts.body,
        appearance: 'none',
        WebkitAppearance: 'none',
        border: 'none',
        backgroundColor: 'transparent',
        color: Theme.Colours.text,
        cursor: 'pointer',
        fontSize: '13px',
        fontWeight: 700,
        lineHeight: 1,
        padding: '0 5px 0 1px',
    },
    waveformPlaceholder: {
        ...Theme.Fonts.body,
        height: '54px',
        display: 'flex',
        alignItems: 'center',
        color: Theme.Colours.text,
        opacity: 0.68,
        textAlign: 'left',
    },
    timeLabel: {
        ...Theme.Fonts.body,
        minWidth: '92px',
        textAlign: 'right',
        color: 'rgba(0, 0, 0, 0.68)',
        fontSize: '13px',
        fontVariantNumeric: 'tabular-nums',
        flexShrink: 0,
    },
    tagTray: {
        display: 'flex',
        alignItems: 'center',
        gap: '0px',
        minWidth: 0,
        overflow: 'hidden',
        whiteSpace: 'nowrap',
    },
    availableTagButton: {
        ...Theme.Fonts.body,
        appearance: 'none',
        WebkitAppearance: 'none',
        flex: '0 0 auto',
        border: '1.5px solid',
        borderRadius: '999px',
        color: Theme.Colours.text,
        cursor: 'pointer',
        fontSize: '12px',
        fontWeight: 700,
        maxWidth: '170px',
        overflow: 'hidden',
        padding: '5px 8px',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
    },
    addTagButton: {
        ...Theme.Fonts.body,
        appearance: 'none',
        WebkitAppearance: 'none',
        flex: '0 0 auto',
        width: '28px',
        height: '28px',
        border: Theme.Colours.border,
        borderRadius: '999px',
        backgroundColor: '#ffffff',
        color: Theme.Colours.text,
        cursor: 'pointer',
        fontSize: '18px',
        fontWeight: 700,
        lineHeight: 1,
        padding: 0,
    },
    newTagInput: {
        ...Theme.Fonts.body,
        flex: '0 0 150px',
        height: '28px',
        boxSizing: 'border-box',
        border: Theme.Colours.border,
        borderRadius: '999px',
        backgroundColor: '#ffffff',
        color: Theme.Colours.text,
        outline: 'none',
        padding: '5px 10px',
    },
};
